import { injectable, decorate, inject } from 'inversify';
import 'reflect-metadata';
import { SERVICE_IDENTIFIER } from 'constants/identifiers';
import { formatDate } from 'utils/date';
import { sapFullDateFormat } from 'constants/dateFormats';
import { get } from 'lodash';
import { downloadFile, extractFilenameFromHeader } from 'utils/file';

@injectable()
class NewCustomerService {
  constructor(apiService) {
    this.apiService = apiService;
  }

  async getCustomers(params, abortSignal) {
    const result = await this.apiService.get('/new_customers/list', params, {
      signal: abortSignal,
    });
    return result.data.data.items;
  }

  async getCustomerInfo(customerId) {
    const result = await this.apiService.get(`/new_customers/${customerId}`);
    return result.data.data.items[0];
  }

  async getLatestShipment(customerId) {
    const result = await this.apiService.get(
      `/new_customers/${customerId}/latest-shipment`,
    );
    return result.data.data.items[0];
  }

  async getCustomerPayments(customerId, abortSignal) {
    const result = await this.apiService.get(
      `/new_customers/${customerId}/payments`,
      undefined,
      { signal: abortSignal },
    );
    return result.data.data.items;
  }

  async getCustomerContracts(customerId, abortSignal) {
    const result = await this.apiService.get(
      `/new_customers/${customerId}/contracts`,
      undefined,
      { signal: abortSignal },
    );
    return result.data.data.items;
  }

  async getCustomerHistory(customerId, abortSignal) {
    const result = await this.apiService.get(
      `/new_customers/${customerId}/history`,
      undefined,
      { signal: abortSignal },
    );
    return result.data.data.items;
  }

  async getCustomerContactsList(customerId, abortSignal) {
    const result = await this.apiService.get(
      `/new_customers/${customerId}/contacts`,
      undefined,
      { signal: abortSignal },
    );
    return result.data.data.items;
  }

  async getCustomerContact(customerId, contactId) {
    const result = await this.apiService.get(
      `/new_customers/${customerId}/contacts/${contactId}`,
    );
    return result.data.data.items[0];
  }

  async getContactVcard(customerId, contactId) {
    const result = await this.apiService.get(
      `/new_customers/${customerId}/contacts/${contactId}/vcard`,
    );
    const filename = 'contact.vcf';
    downloadFile(result.data, filename);
  }

  async addContact(customerId, formData) {
    const result = await this.apiService.post(
      `/new_customers/${customerId}/contacts`,
      formData,
    );
    return result.data.data.items[0];
  }

  async editContact(customerId, contactId, formData) {
    const result = await this.apiService.put(
      `/new_customers/${customerId}/contacts/${contactId}`,
      formData,
    );
    return result.data.data.items[0];
  }

  async createRevisionAct(entityId, data) {
    const { companyCode, period } = data;
    const [dateStart, dateEnd] = period;
    const newDateStart = formatDate(dateStart, sapFullDateFormat);
    const newDateEnd = formatDate(dateEnd, sapFullDateFormat);
    const newCompanyCode = get(companyCode, 'id');
    const responseType = 'blob';

    const newData = {
      dateFrom: newDateStart,
      dateTo: newDateEnd,
      companyCode: newCompanyCode,
    };

    const result = await this.apiService.post(
      `/new_customers/${entityId}/revision_act`,
      newData,
      {
        responseType,
      },
    );

    const header = result.headers['content-disposition'];
    const filename = extractFilenameFromHeader(header);
    downloadFile(result.data, filename);
  }

  async createPaymentCalendar(entityId, data) {
    const { companyCode, date, contract, salesOrganization } = data;
    const newDate = formatDate(date, sapFullDateFormat);
    const newCompanyCode = get(companyCode, 'id');
    const newContract = get(contract, 'contractNumber');
    const newSalesOrganization = get(salesOrganization, 'id');
    const responseType = 'blob';

    const newData = {
      date: newDate,
      companyCode: newCompanyCode,
      contract: newContract,
      salesOrganization: newSalesOrganization,
    };

    const result = await this.apiService.post(
      `/new_customers/${entityId}/payment_calendar`,
      newData,
      {
        responseType,
      },
    );

    const header = result.headers['content-disposition'];
    const filename = extractFilenameFromHeader(header);
    downloadFile(result.data, filename);
  }
}

decorate(inject(SERVICE_IDENTIFIER.ApiService), NewCustomerService, 0);

export default NewCustomerService;
